import {
  OneColumnGroupDto,
  PageDto,
  TabDto,
  TabGroupDto,
  TwoColumnGroupDto,
  WidgetDto,
} from '@asset-metrix/portal-gateway-client-js';

export type WidgetWidthFactor = 0.5 | 1;

/** Prefix for draft-only widgets created via Add Component. */
export const PAGE_EDIT_DUMMY_WIDGET_ID_PREFIX = 'page-edit-dummy-';

export function isPageEditDummyWidgetId(widgetId: string): boolean {
  return widgetId.startsWith(PAGE_EDIT_DUMMY_WIDGET_ID_PREFIX);
}

export type MoveAvailability = {
  canMoveUp: boolean;
  canMoveDown: boolean;
};

type ColumnGroupContent = Exclude<PageDto['contents'][number], TabGroupDto>;

export type PageEditTabPlacement = {
  tabGroupId: string;
  tabs: TabGroupDto['tabs'];
  tabId: string;
  tabGroupKey: string;
};

export type PageEditWidget = {
  widget: WidgetDto;
  widthFactor: WidgetWidthFactor;
  tabPlacement?: PageEditTabPlacement;
};

function toWidthFactor(value: number): WidgetWidthFactor {
  return value <= 0.5 ? 0.5 : 1;
}

type ColumnGroupDto = OneColumnGroupDto | TwoColumnGroupDto;

export function sameMoveScope(a: PageEditWidget, b: PageEditWidget): boolean {
  if (a.tabPlacement == null && b.tabPlacement == null) {
    return true;
  }
  if (a.tabPlacement == null || b.tabPlacement == null) {
    return false;
  }
  return (
    a.tabPlacement.tabGroupId === b.tabPlacement.tabGroupId &&
    a.tabPlacement.tabId === b.tabPlacement.tabId
  );
}

function findWidgetIndex(
  widgets: ReadonlyArray<PageEditWidget>,
  widgetId: string
): number {
  return widgets.findIndex((item) => item.widget.id === widgetId);
}

export function getWidgetMoveAvailability(
  widgets: ReadonlyArray<PageEditWidget>,
  widgetId: string
): MoveAvailability {
  const index = findWidgetIndex(widgets, widgetId);
  if (index === -1) {
    return { canMoveUp: false, canMoveDown: false };
  }

  const current = widgets[index];
  if (current == null) {
    return { canMoveUp: false, canMoveDown: false };
  }

  const previous = widgets[index - 1];
  const next = widgets[index + 1];

  return {
    canMoveUp: previous != null && sameMoveScope(current, previous),
    canMoveDown: next != null && sameMoveScope(current, next),
  };
}

/**
 * Page-body widgets are always removable. Tab widgets are removable only when
 * another draft item still carries the same tabGroupId, so empty tab groups are
 * not dropped from the edit session.
 */
export function canRemoveWidget(
  widgets: ReadonlyArray<PageEditWidget>,
  widgetId: string
): boolean {
  const index = findWidgetIndex(widgets, widgetId);
  if (index === -1) {
    return false;
  }

  const current = widgets[index];
  if (current == null) {
    return false;
  }

  const tabGroupId = current.tabPlacement?.tabGroupId;
  if (tabGroupId == null) {
    return true;
  }

  return widgets.some(
    (item, itemIndex) =>
      itemIndex !== index && item.tabPlacement?.tabGroupId === tabGroupId
  );
}

function createSingleColumnGroup(widget: WidgetDto): OneColumnGroupDto {
  return {
    type: 'singleColumnGroup',
    contents: [widget],
  };
}

function createTwoColumnGroup(
  contents: TwoColumnGroupDto['contents']
): TwoColumnGroupDto {
  return {
    type: 'twoColumnGroup',
    contents,
  };
}

/**
 * Pack ordered widgets into single/two column groups.
 * Mirrors gateway `createColumnWithContent` free-slot pairing: unpaired half-width
 * stays as a one-child `twoColumnGroup`.
 */
function regroupOrderedWidgets(
  items: ReadonlyArray<PageEditWidget>
): ColumnGroupDto[] {
  const regrouped: ColumnGroupDto[] = [];
  let isFreeTwoColumnGroupSlotAvailable = false;

  for (const item of items) {
    if (item.widthFactor <= 0.5) {
      const wrappedWidget = createSingleColumnGroup(item.widget);
      if (isFreeTwoColumnGroupSlotAvailable) {
        const last = regrouped.at(-1);
        if (last?.type === 'twoColumnGroup') {
          last.contents.push(wrappedWidget);
        }
        isFreeTwoColumnGroupSlotAvailable = false;
      } else {
        regrouped.push(createTwoColumnGroup([wrappedWidget]));
        isFreeTwoColumnGroupSlotAvailable = true;
      }
      continue;
    }

    regrouped.push(createSingleColumnGroup(item.widget));
    isFreeTwoColumnGroupSlotAvailable = false;
  }

  return regrouped;
}

/**
 * Rebuild nested page contents for rendering from the flat edit list. (mirroring gateway `mapContentsToDto`)
 */
export function regroupWidgetsToContents(
  widgets: ReadonlyArray<PageEditWidget>
): PageDto['contents'] {
  const contents: PageDto['contents'] = [];
  let index = 0;

  while (index < widgets.length) {
    const current = widgets[index];
    if (current == null) {
      break;
    }

    if (current.tabPlacement == null) {
      const pageBodyRun: PageEditWidget[] = [];
      while (index < widgets.length && widgets[index]?.tabPlacement == null) {
        const item = widgets[index];
        if (item != null) {
          pageBodyRun.push(item);
        }
        index += 1;
      }
      contents.push(...regroupOrderedWidgets(pageBodyRun));
      continue;
    }

    const { tabGroupId, tabs } = current.tabPlacement;
    const tabGroupRun: PageEditWidget[] = [];
    while (
      index < widgets.length &&
      widgets[index]?.tabPlacement?.tabGroupId === tabGroupId
    ) {
      const item = widgets[index];
      if (item != null) {
        tabGroupRun.push(item);
      }
      index += 1;
    }

    const widgetsByTabId = new Map<string, PageEditWidget[]>();
    const tabGroupKeyByTabId = new Map<string, string>();

    for (const item of tabGroupRun) {
      const placement = item.tabPlacement;
      if (placement == null) {
        continue;
      }
      if (!widgetsByTabId.has(placement.tabId)) {
        widgetsByTabId.set(placement.tabId, []);
        tabGroupKeyByTabId.set(placement.tabId, placement.tabGroupKey);
      }
      widgetsByTabId.get(placement.tabId)?.push(item);
    }

    // Keep every tab from metadata, including tabs with no remaining widgets.
    const tabContents: TabDto[] = tabs.map((tab) => ({
      type: 'tab',
      id: tab.value,
      tabGroup: tabGroupKeyByTabId.get(tab.value) ?? tabGroupId,
      contents: regroupOrderedWidgets(widgetsByTabId.get(tab.value) ?? []),
    }));

    contents.push({
      type: 'tabGroup',
      id: tabGroupId,
      tabs,
      contents: tabContents,
    });
  }

  return contents;
}

function collectWidgetsFromColumnGroups(
  contents: ReadonlyArray<ColumnGroupContent>,
  inheritedWidth: number,
  tabPlacement: PageEditTabPlacement | undefined,
  result: PageEditWidget[]
): void {
  for (const content of contents) {
    switch (content.type) {
      case 'widget':
        result.push({
          widget: content,
          widthFactor: toWidthFactor(inheritedWidth),
          tabPlacement,
        });
        break;
      case 'singleColumnGroup':
        collectWidgetsFromColumnGroups(
          content.contents,
          inheritedWidth,
          tabPlacement,
          result
        );
        break;
      case 'twoColumnGroup':
        collectWidgetsFromColumnGroups(
          content.contents,
          0.5,
          tabPlacement,
          result
        );
        break;
      case 'error':
        break;
      default: {
        const exhaustiveCheck: never = content;
        throw new Error(
          `Unsupported page content type: ${String(exhaustiveCheck)}`
        );
      }
    }
  }
}

/**
 * Function to flatten page contents to a list of widgets.
 * @param contents Page contents to flatten
 * @returns Flattened widgets
 */
export function flattenContentsToWidgets(
  contents: ReadonlyArray<PageDto['contents'][number]>
): PageEditWidget[] {
  const widgets: PageEditWidget[] = [];

  for (const content of contents) {
    if (content.type === 'tabGroup') {
      for (const tab of content.contents) {
        const tabPlacement: PageEditTabPlacement = {
          tabGroupId: content.id,
          tabs: content.tabs,
          tabId: tab.id,
          tabGroupKey: tab.tabGroup,
        };
        collectWidgetsFromColumnGroups(tab.contents, 1, tabPlacement, widgets);
      }
      continue;
    }

    collectWidgetsFromColumnGroups([content], 1, undefined, widgets);
  }

  return widgets;
}
